/* ===============================
   iOS Back/Forward Scroll Restore
   =============================== */
(() => {
  const ua = navigator.userAgent || "";
  const isIOS = /iP(ad|hone|od)/.test(ua);
  if (!isIOS) return;

  try {
    const style = document.createElement("style");
    style.textContent = "html{scroll-behavior:auto!important;}";
    document.head.appendChild(style);
  } catch {}

  const key = "scroll-pos:" + location.pathname + location.search;

  addEventListener("pagehide", () => {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({ x: scrollX, y: scrollY })
      );
    } catch {}
  });

  addEventListener("pageshow", (e) => {
    const nav = (performance.getEntriesByType?.("navigation") || [])[0];
    const isBFCache = e.persisted || (nav && nav.type === "back_forward");
    if (!isBFCache) return;

    try {
      const saved = sessionStorage.getItem(key);
      if (!saved) return;
      const pos = JSON.parse(saved);
      setTimeout(() => scrollTo(pos.x || 0, pos.y || 0), 0);
    } catch {}
  });

  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }
})();

/* ===============================
   Overlay / Modal Scroll-Lock
   =============================== */
(() => {
  const OVERLAY_SEL = [
    '[aria-modal="true"]',
    '[role="dialog"]',
    '.halo-modal.is-show',
    '.halo-sidebar.open',
    '.drawer.is-open',
    '.sidebarCart.is-open',
    '.search-modal.is-open',
    '.facet-drawer.is-open',
    '.quickShopPopup.is-open',
    '.modal.is-active',
    '.jdgm-write-review-modal',
    '.jdgm-write-review-modal__content'
  ].join(",");

  const html = document.documentElement;
  const body = document.body;
  const host = document.querySelector(".halo-wrapper") || body;

  const isVisible = (el) => {
    if (!el) return false;
    const cs = getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden" || +cs.opacity === 0) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
  };
  const getVisibleOverlays = () => [...document.querySelectorAll(OVERLAY_SEL)].filter(isVisible);
  const getTopOverlay = () => {
    const list = getVisibleOverlays();
    return list.length ? list[list.length - 1] : null;
  };
  const closestScrollableInsideOverlay = (target, overlay) => {
    let el = target instanceof Element ? target : null;
    while (el && el !== overlay && overlay.contains(el)) {
      const cs = getComputedStyle(el);
      const oy = cs.overflowY, ox = cs.overflowX;
      const canY = (oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight;
      const canX = (ox === "auto" || ox === "scroll") && el.scrollWidth > el.clientWidth;
      if (canY || canX) return el;
      el = el.parentElement;
    }
    return overlay;
  };

  let activeOverlay = null;
  let guardsOn = false;

  const enableGuards = (on) => {
    if (guardsOn === on) return;
    guardsOn = on;
    const opts = { passive: false, capture: true };
    const m = on ? "addEventListener" : "removeEventListener";
    const outsideGuard = (e) => {
      if (!activeOverlay) return;
      const inside = e.target?.closest?.(OVERLAY_SEL);
      if (!inside) { e.preventDefault(); e.stopPropagation(); return false; }
      const scrollHost = closestScrollableInsideOverlay(e.target, activeOverlay);
      const canScroll = scrollHost && (scrollHost.scrollHeight > scrollHost.clientHeight || scrollHost.scrollWidth > scrollHost.clientWidth);
      if (!canScroll) { e.preventDefault(); e.stopPropagation(); return false; }
    };
    const keyGuard = (e) => {
      if (!activeOverlay) return;
      if (!new Set([" ", "Spacebar", "PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End"]).has(e.key)) return;
      const inside = document.activeElement?.closest?.(OVERLAY_SEL);
      if (!inside) { e.preventDefault(); e.stopPropagation(); return false; }
    };

    // bind/unbind
    window[m]("wheel", outsideGuard, opts);
    document[m]("touchmove", outsideGuard, opts);
    document[m]("wheel", outsideGuard, opts);
    document[m]("keydown", keyGuard, opts);

    // store on window so GC won’t kill closures on remove
    if (on) {
      window.__overlayOutsideGuard = outsideGuard;
      window.__overlayKeyGuard = keyGuard;
    }
  };

  const setLock = (on) => {
    [html, body, host].forEach((n) => n && n.classList.toggle("is-overlay-open", on));
    enableGuards(on);
  };

  const reevaluate = () => {
    activeOverlay = getTopOverlay();
    setLock(!!activeOverlay);
  };

  // Single MutationObserver + RAF debounce
  const mo = new MutationObserver(() => {
    cancelAnimationFrame(window.__overlayRaf);
    window.__overlayRaf = requestAnimationFrame(reevaluate);
  });
  mo.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style", "aria-hidden", "hidden"]
  });

  document.addEventListener("click", (e) => {
    if (e.target.closest('[aria-label="Close"], .close, .modal__close, .drawer__close, .jdgm-write-review-modal__close-btn, .jdgm-write-review-modal__nav-btn-close')) {
      requestAnimationFrame(reevaluate);
    }
  });
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") requestAnimationFrame(reevaluate); });
  addEventListener("resize", reevaluate, { passive: true });
  addEventListener("orientationchange", reevaluate, { passive: true });

  // first pass
  reevaluate();
})();

/* ===============================
   Discount Popup (one-time per tab)
   =============================== */
(() => {
  "use strict";
  if (window.__discountPopupInit) return;
  window.__discountPopupInit = true;

  const path = location.pathname;
  if (/^\/(checkout|orders|account)/i.test(path)) return;

  const d = document;
  const qs = (s, r) => (r || d).querySelector(s);
  const qsa = (s, r) => (r || d).querySelectorAll(s);
  const getParam = (n) => new URLSearchParams(location.search).get(n);
  const getCookie = (name) => {
    const m = d.cookie.match(new RegExp("(?:^|; )" + name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&") + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  };
  const extractCodeFromPath = () => {
    const m = path.match(/\/discount\/([^\/\?]+)/i);
    return m && m[1] ? decodeURIComponent(m[1]) : null;
  };
  const cleanUrl = () => {
    const p = path.replace(/\/discount\/[^\/\?]+/i, "/");
    const u = location.origin + p + (location.search ? location.search.replace(/([?&])(discount|coupon|code)=[^&]*/gi, "$1").replace(/[?&]$/, "") : "") + location.hash;
    try { history.replaceState({}, d.title, u); } catch {}
  };

  const hasSeen = () => { try { return sessionStorage.getItem("discountPopupSeen") === "1"; } catch { return false; } };
  const markSeen = () => { try { sessionStorage.setItem("discountPopupSeen", "1"); } catch {} };
  const saveCode = (c) => { try { sessionStorage.setItem("appliedDiscountCode", c); } catch {} };
  const readSavedCode = () => { try { return sessionStorage.getItem("appliedDiscountCode"); } catch { return null; } };

  const POPUP_ID = "discount-popup";
  const CODE_ID  = "discount-code-label";
  const ensureMarkup = () => {
    let el = d.getElementById(POPUP_ID);
    if (el) return el;
    el = d.createElement("div");
    el.id = POPUP_ID;
    el.className = "discount-popup";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-modal", "true");
    el.setAttribute("aria-labelledby", "discount-title");
    el.setAttribute("aria-live", "polite");
    el.setAttribute("aria-hidden", "true");
    el.setAttribute("tabindex", "-1");
    el.innerHTML =
      '<div class="discount-popup__content" role="document">' +
      '<button class="discount-popup__close" type="button" aria-label="Închide" data-discount-close>&times;</button>' +
      '<h3 id="discount-title" class="discount-popup__title">Cod de reducere aplicat</h3>' +
      '<p class="discount-popup__text">Codul <strong id="' + CODE_ID + '"></strong> a fost preluat. Reducerea se aplică automat la checkout.</p>' +
      '<button class="discount-popup__cta" type="button" data-discount-close>Continuă cumpărăturile</button>' +
      "</div>";
    d.body.appendChild(el);
    return el;
  };

  let escBound = null;
  const onEsc = (e) => { if (e.key === "Escape") hidePopup(); };

  const showPopup = (code) => {
    const el = ensureMarkup();
    if (el.parentNode !== d.body) d.body.appendChild(el);
    const label = d.getElementById(CODE_ID);
    if (label) label.textContent = code || "";

    el.classList.add("is-visible");
    el.setAttribute("aria-hidden", "false");
    d.documentElement.classList.add("modal-open");
    d.body.classList.add("modal-open");

    qsa("[data-discount-close]", el).forEach((btn) => btn.addEventListener("click", hidePopup, { once: true }));
    el.addEventListener("click", (e) => { if (e.target === el) hidePopup(); });

    escBound = escBound || onEsc;
    d.addEventListener("keydown", escBound);

    el.focus();
    markSeen();
  };

  const hidePopup = () => {
    const el = d.getElementById(POPUP_ID);
    if (!el) return;
    el.classList.remove("is-visible");
    el.setAttribute("aria-hidden", "true");
    d.documentElement.classList.remove("modal-open");
    d.body.classList.remove("modal-open");
    d.removeEventListener("keydown", escBound || onEsc);
  };

  const urlCode = getParam("discount") || getParam("coupon") || getParam("code") || extractCodeFromPath();
  if (urlCode) { saveCode(urlCode); cleanUrl(); }

// Înlocuiește resolveCode cu varianta asta:
const resolveCode = () => {
  // arată DOAR dacă a venit din URL în această sesiune
  const saved = readSavedCode();        // setat doar când ai avut parametru/discount în URL
  return saved || null;                 // ignoră cookie-ul ca sursă
};

  const maybeShow = () => {
    if (hasSeen()) return;
    const code = resolveCode();
    if (!code) return;
    showPopup(code);
  };

  if (d.readyState === "loading") {
    d.addEventListener("DOMContentLoaded", () => { maybeShow(); setTimeout(maybeShow, 200); }, { once: true });
  } else {
    maybeShow(); setTimeout(maybeShow, 200);
  }
})();


















/* ===============================
   Salveaza locatia in meniul de mobile
   =============================== */
(function() {
  "use strict";

  const KEY_LAST_PATH = "mobileMenu.LAST_PATH";
  const KEY_LAST_MENU = "mobileMenu.LAST_MENU_PATH";
  const MENU_ROOT_SEL = ".site-nav-mobile.nav.menu-custom-mobile";

  const norm = (u) => (u || "").replace(/[?#].*$/,"").replace(/\/+$/,"") || "/";

  // --- 0) La load: memorizeaza DOAR /collections si /products; altfel reseteaza ---
  window.addEventListener("DOMContentLoaded", () => {
    try {
      const p = norm(location.pathname);

      const isCollections = /^\/collections(\/|$)/.test(p);
      const isProducts    = /^\/products(\/|$)/.test(p);

      if (isCollections || isProducts) {
        sessionStorage.setItem(KEY_LAST_PATH, p);

        // Dacă e colecție, putem folosi path-ul și pentru meniu.
        if (isCollections) {
          sessionStorage.setItem(KEY_LAST_MENU, p);
        }
        // Dacă e produs: NU suprascriem LAST_MENU (menținem ultimul context de meniu).
      } else {
        // Orice altă pagină -> reset complet (cerința ta)
        sessionStorage.removeItem(KEY_LAST_PATH);
        sessionStorage.removeItem(KEY_LAST_MENU);
      }
    } catch(e){}
  });

  // --- 1) Clic în meniul mobil: setăm ambele chei cu path-ul din meniu ---
  document.addEventListener("click", function(e){
    const root = document.querySelector(MENU_ROOT_SEL);
    if (!root || !root.contains(e.target)) return;

    const a = e.target.closest('a[href^="/"]');
    if (!a) return;

    const p = norm(a.getAttribute("href"));
    if (p === "/") return; // nu memorăm home-ul

    try {
      sessionStorage.setItem(KEY_LAST_PATH, p);
      sessionStorage.setItem(KEY_LAST_MENU, p); // linkurile din meniu sunt safe ca "menu context"
    } catch(e){}
  }, { passive:true, capture:true });

  // --- 2) La deschiderea meniului, deschidem contextul salvat sau prima pagină din meniu ---
  document.addEventListener("click", function(){
    let tries = 0, done = false;
    function attempt() {
      if (done) return;
      tries++;
      const root = document.querySelector(MENU_ROOT_SEL);
      if (root && isMenuOpen(root)) {
        done = true;
        const target = sessionStorage.getItem(KEY_LAST_MENU);
        if (target) {
          openToPath(root, target);
        } else {
          highlightFirstMenuLink(root); // fallback: prima pagină din meniu
        }
        return;
      }
      if (tries < 5) setTimeout(attempt, 100); // ~400ms total, lejer
    }
    setTimeout(attempt, 60);
  }, true);

  // --- Helpers ---------------------------------------------------------------

  function isMenuOpen(root) {
    const visible = root.querySelector(".list-menu.is-visible");
    if (visible) return true;
    const r = root.getBoundingClientRect();
    return !!(r.width || r.height);
  }

  function openToPath(root, path) {
    const link = findLink(root, path);
    if (!link) {
      // Dacă nu mai există (URL schimbat), revenim la prima pagină din meniu.
      highlightFirstMenuLink(root);
      return;
    }
    openParentsOnce(link);
    setTimeout(() => {
      try {
        link.classList.add("is-active");
        link.setAttribute("aria-current","page");
        link.scrollIntoView({ block:"center", behavior:"smooth" });
      } catch(e){}
    }, 120);
  }

  function highlightFirstMenuLink(root) {
    // Prima "pagină din meniu" = primul <a href="/..."> din lista nivel 1
    const first = root.querySelector('.list-menu.is-visible a[href^="/"]') ||
                  root.querySelector('a[href^="/"]');
    if (!first) return;

    // Închidem dropdown-urile deschise (rămânem în root)
    root.querySelectorAll('.menu-lv__dropdownmenu, .menu-lv__dropdownmenu-2').forEach(dd=>{
      // dacă tema ta are clase pentru "open", le poți elimina aici dacă e nevoie
      // lăsăm default: ne bazăm pe starea implicită "închis"
    });

    try {
      first.classList.add("is-active");
      first.setAttribute("aria-current","page");
    } catch(e){}
  }

  function findLink(root, path) {
    const t = norm(path);
    if (!t || t === "/") return null;

    // 1) match exact
    let a = root.querySelector(`a[href="${t}"]`);
    if (a) return a;

    // 2) match exact fără slash final
    const links = root.querySelectorAll('a[href^="/"]');
    for (const el of links) if (norm(el.getAttribute("href")) === t) return el;

    // 3) fallback startsWith doar dacă are cel puțin un segment
    if (t.length > 1 && t.includes("/")) {
      for (const el of links) {
        const h = norm(el.getAttribute("href"));
        if (h !== "/" && h.startsWith(t)) return el;
      }
    }
    return null;
  }

  function openParentsOnce(linkEl) {
    const chain = [];
    let li = linkEl.closest(".menu-lv-item");
    const seen = new Set();

    while (li && !seen.has(li)) {
      seen.add(li);
      const parentDropdown = li.closest(".menu-lv-item.dropdown");
      if (parentDropdown) {
        chain.push(parentDropdown);
        li = parentDropdown.parentElement ? parentDropdown.parentElement.closest(".menu-lv-item") : null;
      } else {
        li = li.parentElement ? li.parentElement.closest(".menu-lv-item") : null;
      }
    }

    chain.reverse().forEach((node) => {
      const dd = node.querySelector(':scope > .menu-lv__dropdownmenu, :scope > .menu-lv__dropdownmenu-2');
      const opened = dd && isShown(dd);
      if (!opened) {
        const icon = node.querySelector(':scope > a .icon-dropdown, :scope > .menu-lv-1__action .icon-dropdown, :scope > .menu-lv-2__action .icon-dropdown');
        if (icon) {
          icon.dispatchEvent(new MouseEvent("click", { bubbles:true, cancelable:true }));
        }
      }
    });
  }

  function isShown(el) {
    const cs = getComputedStyle(el);
    return cs.display !== "none" && cs.visibility !== "hidden";
  }
})();















/* ===============================
   Redirect click swatch-uri culori
   =============================== */
document.addEventListener('DOMContentLoaded', function () {
  // Heuristică pentru elemente Swatch King + ale tale
  const isSwatchTrigger = (el) => {
    return !!el.closest([
      '.swatch-view-item',
      '.swatch-view',
      '.swatch-more-button',
      // Swatch King – acoperim prefixele/familiile uzuale
      '.sk-swatch', '.sk-option', '.sk-options', '.sk-swatch-list',
      '.swatch_options', '.swatch-option', '.swatch-king',
      '[data-swatch]', '[data-swatch-option]', '[data-swatch-name]'
    ].join(','));
  };

  // NU intervenim pe swatch-urile formularului principal de produs
  const isInsideMainProductForm = (el) => {
    return !!el.closest([
      '#product-form', '.product-form', '[data-product-form]',
      'form[action*="/cart"]',
      '.product__info-wrapper', '.productView-details', '.product-single__info'
    ].join(','));
  };

  // Găsește URL de produs din card (titlu/poză)
  const findProductUrl = (start) => {
    const card = start.closest(
      '.product-card, .product-item, .card, .grid__item, .productgrid--item, [data-product-id], [data-product]'
    ) || document;
    const link =
      card.querySelector('a[href*="/products/"]:not([href*="#"])') ||
      card.querySelector('.card-title a, .product-title a, a.full-unstyled-link, a.product-image');
    return link ? link.getAttribute('href') : null;
  };

  // Delegare în CAPTURĂ: ne punem în fața temei + app-ului
  document.addEventListener('click', function (e) {
    const target = e.target;

    // Dacă s-a făcut click chiar pe un link de produs, nu intervenim
    if (target.closest('a[href*="/products/"]')) return;

    // Intervenim doar pe clickuri de swatch (inclusiv +x culori)
    if (!isSwatchTrigger(target)) return;

    // Dacă e în formularul principal al paginii de produs, nu intervenim (lăsăm schimbarea de variantă)
    if (isInsideMainProductForm(target)) return;

    const url = findProductUrl(target);
    if (!url) return;

    // Tăiem orice handler al temei/app-ului și redirecționăm
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    // Unele implementări ascultă și pe mousedown/touchstart – preîntâmpinăm
    const abort = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
    ['mousedown','touchstart'].forEach(type => {
      document.addEventListener(type, abort, { capture: true, once: true });
    });

    window.location.href = url;
  }, true); // capture = true
});